import {Dialog} from '@ui/overlays/dialog/dialog';
import {DialogHeader} from '@ui/overlays/dialog/dialog-header';
import {DialogBody} from '@ui/overlays/dialog/dialog-body';
import {DialogFooter} from '@ui/overlays/dialog/dialog-footer';
import {Form} from '@ui/forms/form';
import {FormTextField} from '@ui/forms/input-field/text-field/text-field';
import {FormSelect} from '@ui/forms/select/select';
import {Button} from '@ui/buttons/button';
import {useForm} from 'react-hook-form';
import {useSubmitFeedback} from './use-submit-feedback';
import {Item} from '@ui/forms/listbox/item';
import {Trans} from '@ui/i18n/trans';
import {DialogTrigger} from '@ui/overlays/dialog/dialog-trigger';

interface FeedbackDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  message: string;
  severity: 'Minor' | 'Bug' | 'Blocker';
}

export function FeedbackDialog({isOpen, onClose}: FeedbackDialogProps) {
  const form = useForm<FormData>({
    defaultValues: {
      severity: 'Minor',
      message: '',
    },
  });

  const submitFeedback = useSubmitFeedback();

  const handleSubmit = (data: FormData) => {
    submitFeedback.mutate(data, {
      onSuccess: () => {
        form.reset();
        onClose();
      },
    });
  };

  return (
    <DialogTrigger type="modal" isOpen={isOpen} onOpenChange={(open) => {
      if (!open) onClose();
    }}>
      <Dialog size="md">
        <DialogHeader>
          <Trans message="Send Feedback" />
        </DialogHeader>
        <DialogBody>
          <Form form={form} onSubmit={handleSubmit}>
            <FormSelect
              name="severity"
              selectionMode="single"
              label={<Trans message="Type" />}
              description={
                <Trans message="How severe is this issue or feedback?" />
              }
              required
            >
              <Item value="Minor">
                <Trans message="Minor - Suggestion or improvement" />
              </Item>
              <Item value="Bug">
                <Trans message="Bug - Something isn't working" />
              </Item>
              <Item value="Blocker">
                <Trans message="Blocker - Critical issue preventing use" />
              </Item>
            </FormSelect>

            <FormTextField
              name="message"
              label={<Trans message="Message" />}
              placeholder="Describe your feedback or issue..."
              inputElementType="textarea"
              rows={6}
              required
              minLength={10}
              maxLength={5000}
              autoFocus
            />

            <DialogFooter>
              <Button onClick={onClose} variant="outline" disabled={submitFeedback.isPending}>
                <Trans message="Cancel" />
              </Button>
              <Button
                type="submit"
                variant="flat"
                color="primary"
                disabled={submitFeedback.isPending}
              >
                <Trans message="Send Feedback" />
              </Button>
            </DialogFooter>
          </Form>
        </DialogBody>
      </Dialog>
    </DialogTrigger>
  );
}
